/**
 * Zawijanie etykiet w SVG — dla nazw modeli na schemacie elektrycznym.
 *
 * ═══ PO CO ═══
 * SVG nie zawija tekstu sam. Nazwy falowników bywają długie („Deye
 * SUN-6K-SG05LP1-EU") i wychodziły poza prostokąt urządzenia albo były ucinane
 * po ustalonej liczbie znaków — w połowie słowa i bez żadnego znaku, że coś
 * ucięto. Na dokumencie wysyłanym klientowi wygląda to na usterkę, a nie na
 * skrót.
 *
 * ═══ DLACZEGO SZACUJEMY SZEROKOŚĆ, A NIE MIERZYMY ═══
 * Zmierzenie tekstu wymaga `canvas` albo `getComputedTextLength`, czyli
 * przeglądarki. Ten sam schemat renderuje się w prerenderze SEO (Node, bez DOM),
 * więc pomiar musiałby mieć dwie ścieżki — a wtedy wydruk i podgląd potrafiłyby
 * się różnić. Szacowanie działa wszędzie tak samo i przy zapasie poniżej daje
 * ten sam wynik w obu środowiskach.
 */

/**
 * Średnia szerokość znaku jako ułamek rozmiaru czcionki.
 *
 * 0,62 zamiast typowych 0,5–0,55 dla zwykłego tekstu, bo nazwy modeli to
 * WIELKIE LITERY I CYFRY, a te są w krojach bezszeryfowych wyraźnie szersze niż
 * małe litery. Zaniżenie tego współczynnika oznacza tekst wystający poza kształt,
 * czyli dokładnie usterkę, którą ten moduł ma usunąć — dlatego szacujemy z górką.
 */
const SZEROKOSC_ZNAKU = 0.62

/** Przedrostki, które nie niosą informacji, bo obok stoi już nagłówek „FALOWNIK". */
const ZBEDNY_PRZEDROSTEK = /^(Falownik|Inwerter)\s*[-–]?\s*/i

/**
 * Dzieli tekst na kawałki, które wolno rozdzielić.
 *
 * Łamiemy po spacjach ORAZ po myślnikach, bo kody modeli („SG05LP1-EU") nie mają
 * spacji i bez tego byłyby jednym nierozerwalnym słowem. Myślnik zostaje na
 * końcu linii — inaczej czytelnik nie wie, czy to część nazwy, czy znak podziału.
 */
const naCzesci = (tekst) => tekst
  .split(/\s+/)
  .flatMap((slowo) => slowo.split(/(?<=-)/))
  .filter(Boolean)

const zlamNaLinie = (czesci, budzet) => {
  const linie = []
  let biezaca = ''

  for (const czesc of czesci) {
    const kandydat = biezaca && !biezaca.endsWith('-') ? `${biezaca} ${czesc}` : biezaca + czesc

    if (kandydat.length <= budzet) {
      biezaca = kandydat
      continue
    }
    if (biezaca) linie.push(biezaca)

    // Kawałek dłuższy niż cała linia (np. kod bez myślników) — tniemy go twardo,
    // bo alternatywą jest tekst wychodzący poza kształt.
    let reszta = czesc
    while (reszta.length > budzet) {
      linie.push(reszta.slice(0, budzet))
      reszta = reszta.slice(budzet)
    }
    biezaca = reszta
  }

  if (biezaca) linie.push(biezaca)
  return linie
}

/**
 * Nazwa rozłożona na linie mieszczące się w zadanej szerokości.
 *
 * Zwraca `{ linie, rozmiar }`. Najpierw próbuje zadanego rozmiaru czcionki;
 * jeśli tekst nie mieści się w dozwolonej liczbie linii, ZMNIEJSZA czcionkę
 * zamiast od razu ucinać. Skrócenie nazwy modelu gubi informację, o którą klient
 * może zapytać, a mniejsza czcionka nie gubi nic.
 *
 * Wielokropek pojawia się dopiero, gdy nazwa nie mieści się nawet w najmniejszym
 * rozmiarze — i wtedy jest widoczny, więc czytelnik wie, że to skrót.
 */
export const zawinNazwe = (nazwa, {
  szerokosc, rozmiar = 11.5, minRozmiar = 9, maksLinii = 2,
} = {}) => {
  // Samotny myślnik między słowami („Inwerter - HUAWEI - SUN 2000-12KTL-M5”) to separator
  // z nazwy sklepu, nie część kodu. Zostawiony dawał po złamaniu „HUAWEI -SUN 2000-”.
  const czysta = String(nazwa || '').replace(ZBEDNY_PRZEDROSTEK, '')
    .replace(/\s+[-–—]\s+/g, ' ').trim()
  if (!czysta) return { linie: [], rozmiar }
  if (!szerokosc || szerokosc <= 0) return { linie: [czysta], rozmiar }

  const czesci = naCzesci(czysta)

  for (let r = rozmiar; r >= minRozmiar; r -= 0.5) {
    const budzet = Math.floor(szerokosc / (r * SZEROKOSC_ZNAKU))
    if (budzet < 3) continue
    const linie = zlamNaLinie(czesci, budzet)
    if (linie.length <= maksLinii) return { linie, rozmiar: r }
  }

  // Nie zmieściło się w żadnym rozmiarze — bierzemy najmniejszy i ucinamy
  // OSTATNIĄ dozwoloną linię, zostawiając widoczny wielokropek.
  const budzet = Math.max(3, Math.floor(szerokosc / (minRozmiar * SZEROKOSC_ZNAKU)))
  const linie = zlamNaLinie(czesci, budzet).slice(0, maksLinii)
  const ostatnia = linie[maksLinii - 1]
  if (ostatnia) linie[maksLinii - 1] = `${ostatnia.slice(0, Math.max(1, budzet - 1)).trimEnd()}…`
  return { linie, rozmiar: minRozmiar }
}
